import { PROVIDERS } from '../data/providers';
import { PROVIDERS_BILLING_URLS } from '../data/models';
import { t } from '../modules/i18n';
import { getKey as getApiKey } from '../modules/api-keys';

/* Re-exported for back-compat with callers that imported getKey from here.
 * The authoritative implementation lives in modules/api-keys.ts which
 * reads from sessionStorage first (synced from backend) and falls back to
 * localStorage for anonymous / legacy users. */
export function getKey(provider) {
  return getApiKey(provider);
}

/* Parse provider error responses into friendly messages */
export function parseProviderError(provider, status, bodyText) {
  let parsed = null;
  try { parsed = JSON.parse(bodyText); } catch (_) {}
  // Try to extract a useful message
  let raw = "";
  if (parsed) {
    raw = parsed.error?.message
       || parsed.error?.error?.message
       || parsed.message
       || parsed.error
       || JSON.stringify(parsed).slice(0, 200);
  } else {
    raw = bodyText.slice(0, 200);
  }
  if (typeof raw !== "string") raw = JSON.stringify(raw);

  const low = raw.toLowerCase();
  let friendly = raw;
  let action = null;

  // Common patterns across providers
  if (low.includes("credit balance") || low.includes("insufficient_quota") ||
      low.includes("billing") || low.includes("quota") ||
      (low.includes("balance") && low.includes("low"))) {
    friendly = t("err.lowbalance", { provider });
    action = PROVIDERS_BILLING_URLS[provider] || null;
  } else if (low.includes("invalid api key") || low.includes("invalid_api_key") ||
             low.includes("incorrect api key") || status === 401 ||
             low.includes("authentication") || low.includes("unauthorized")) {
    friendly = t("err.invalidkey", { provider });
    action = PROVIDERS[provider]?.keyUrl || null;
  } else if (status === 429 || low.includes("rate limit") || low.includes("too many requests")) {
    friendly = t("err.ratelimit", { provider });
  } else if (status === 404 || low.includes("not found") || low.includes("model")) {
    if (low.includes("model")) friendly = t("err.model", { provider, raw });
  } else if (status === 403 || low.includes("forbidden") || low.includes("permission")) {
    friendly = t("err.forbidden", { provider, raw });
  }

  return { friendly, action, raw, status };
}

export class ProviderError extends Error {
  constructor(provider, status, bodyText) {
    const info = parseProviderError(provider, status, bodyText);
    super(info.friendly);
    this.provider = provider;
    this.status = status;
    this.raw = info.raw;
    this.action = info.action;
    this.friendly = info.friendly;
  }
}

/* Test a provider's connection — returns { ok, models?, error? }
   Each provider's "list models" endpoint is used so we can both verify the
   key and surface what model IDs are actually available to this account. */
export async function testProvider(providerId) {
  const p = PROVIDERS[providerId];
  const key = getApiKey(providerId);
  if (!key) return { ok: false, error: t("settings.notset") };

  try {
    if (providerId === "google") {
      const r = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(key)}`);
      if (!r.ok) {
        const txt = await r.text();
        return { ok: false, error: parseProviderError("Google", r.status, txt).friendly, status: r.status };
      }
      const data = await r.json();
      const models = (data.models || [])
        .filter(m => (m.supportedGenerationMethods || []).includes("generateContent"))
        .map(m => m.name.replace(/^models\//, ""))
        .sort();
      return { ok: true, models };
    }

    if (providerId === "anthropic") {
      const r = await fetch("https://api.anthropic.com/v1/models", {
        headers: {
          "x-api-key": key,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
      });
      if (!r.ok) {
        const txt = await r.text();
        return { ok: false, error: parseProviderError("Anthropic", r.status, txt).friendly, status: r.status };
      }
      const data = await r.json();
      const models = (data.data || []).map(m => m.id).sort();
      return { ok: true, models };
    }

    // OpenAI-compatible: OpenAI, xAI, Mistral, Groq, OpenRouter, DeepSeek
    const baseMap = {
      openai:     "https://api.openai.com/v1/models",
      xai:        "https://api.x.ai/v1/models",
      mistral:    "https://api.mistral.ai/v1/models",
      groq:       "https://api.groq.com/openai/v1/models",
      openrouter: "https://openrouter.ai/api/v1/models",
      deepseek:   "https://api.deepseek.com/v1/models",
    };
    const url = baseMap[providerId];
    if (!url) return { ok: false, error: "Unknown provider" };
    const r = await fetch(url, { headers: { "Authorization": `Bearer ${key}` } });
    if (!r.ok) {
      const txt = await r.text();
      return { ok: false, error: parseProviderError(p.label, r.status, txt).friendly, status: r.status };
    }
    const data = await r.json();
    const models = (data.data || []).map(m => m.id).sort();
    return { ok: true, models };
  } catch (e) {
    return { ok: false, error: e.message || String(e) };
  }
}

export async function callOpenAICompat(providerLabel, baseUrl, key, model, prompt, signal, opts) {
  // o1 family doesn't accept max_tokens — uses max_completion_tokens; also no temperature
  const isO1 = /^o1/i.test(model);
  const maxOut = (opts && opts.maxOutTokens) || 8192;
  // Build content. With no parts → bare string. With image parts → use
  // OpenAI-style content array with `image_url` parts. Works for vision
  // models on OpenAI / xAI / OpenRouter (per the OpenAI spec). Other
  // OpenAI-compat providers without vision will reject this shape; the
  // dispatcher already filters parts to only the vision-capable ones.
  const parts = (opts && opts.parts) || [];
  let content;
  if (parts.length === 0) {
    content = prompt;
  } else {
    content = [];
    for (const a of parts) {
      if (a.kind === 'image' && a.base64) {
        content.push({
          type: 'image_url',
          image_url: { url: `data:${a.mime || 'image/png'};base64,${a.base64}` },
        });
      }
      // PDFs are not yet a first-class part in OpenAI chat completions;
      // dispatcher already filters them out for non-PDF providers.
    }
    content.push({ type: 'text', text: prompt });
  }
  const body = {
    model,
    messages: [{ role: "user", content }],
  };
  /* Per-model `maxOut` lives in src/data/models.ts. Synthesis specifically
     overrides via opts.maxOutTokens — we ask for the model's ceiling because
     consolidations are the most likely call to hit a wall. Old behaviour
     (hardcoded 8192) silently truncated long syntheses without warning. */
  if (isO1) body.max_completion_tokens = maxOut;
  else body.max_tokens = maxOut;
  const r = await fetch(baseUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${key}`,
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok) {
    const err = await r.text();
    throw new ProviderError(providerLabel, r.status, err);
  }
  const data = await r.json();
  const u = data.usage || {};
  const finishReason = data.choices?.[0]?.finish_reason || "";
  return {
    text: data.choices?.[0]?.message?.content || "",
    inTokens:  u.prompt_tokens     || u.input_tokens  || 0,
    outTokens: u.completion_tokens || u.output_tokens || 0,
    finishReason,
    truncated: finishReason === "length",
  };
}
